import { formatDisplayTime, toApiTime, toLookupOption } from "../../services/otNoteService";

const has = (arr, value) => Array.isArray(arr) && arr.includes(value);

export const buildOtNoteSavePayload = ({
  formValues,
  selectedPatient,
  surgeryList,
  asstDoctorList,
  asstSurgeonList,
  staffList,
  userId,
  isOtReserved,
}) => {
  const drains = formValues.drains || [];
  const complicated = formValues.complicated || [];
  const anaesthesiaIds = (formValues.anaesthesiaType || [])
    .map((o) => o?.id ?? o?.value)
    .filter((id) => id != null && id !== "")
    .join(",");

  const assistants = [
    ...(asstDoctorList || []).map((r) => ({
      asstDoctorId: Number(r.doctorId || r.code) || 0,
      empId: 0,
      isDoc: true,
      isSurgeon: false,
      charges: 0,
    })),
    ...(asstSurgeonList || []).map((r) => ({
      asstDoctorId: Number(r.doctorId || r.code) || 0,
      empId: 0,
      isDoc: false,
      isSurgeon: true,
      charges: 0,
    })),
    ...(staffList || []).map((r) => ({
      asstDoctorId: 0,
      empId: Number(r.empId || r.code) || 0,
      isDoc: false,
      isSurgeon: false,
      charges: 0,
    })),
  ].filter((a) => a.asstDoctorId > 0 || a.empId > 0);

  const surgeryIds = (surgeryList || [])
    .map((r) => Number(r.surgeryId || r.code))
    .filter((id) => id > 0);

  const durationRaw = formValues.durationMin;
  const duration =
    durationRaw === "" || durationRaw == null ? null : Number(durationRaw) || 0;

  return {
    regId: Number(selectedPatient.regId),
    opdIpdId: Number(selectedPatient.opdIpdId),
    opdIpd: Boolean(selectedPatient.opdIpd),
    otSheetNo: formValues.otSheetNo || null,
    surgeryDate: formValues.surgeryDate || null,
    surgeryStartTime: toApiTime(formValues.startTime),
    surgeryEndTime: toApiTime(formValues.endTime),
    duration,
    otId: formValues.theatreName?.id ?? formValues.theatreName?.value ?? null,
    otTableId: formValues.tableName?.id ?? formValues.tableName?.value ?? null,
    surgeonId: formValues.surgeonName?.id ?? formValues.surgeonName?.value ?? null,
    anaesthetistId:
      formValues.anaesthetistName?.id ?? formValues.anaesthetistName?.value ?? null,
    anaesthesiaTypeId: anaesthesiaIds || null,
    otResultId: formValues.otResult?.id ?? formValues.otResult?.value ?? null,
    surgeryNotes: formValues.surgeryNotesText || "",
    anaesthesiaNotes: formValues.anaesthetistNotesText || "",
    remarks: formValues.remarkSurgeryPlan || "",
    isOtReserved: Boolean(isOtReserved),
    userId: Number(userId) || 1,
    recoveryObservations: formValues.recoveryText || "",
    surgeonCharges: 0,
    anaesthetistCharges: 0,
    otUsageCharges: 0,
    intraOpNotes: formValues.surgeryNotesIntraOpText || "",
    drainCommug: has(drains, "corrugated"),
    drainChest: has(drains, "chest"),
    drainAbd: has(drains, "abdominal"),
    drainRv: has(drains, "rv"),
    fpExplo: formValues.finalProcedure === "exploratory",
    fpPallative: formValues.finalProcedure === "palliative",
    fpCurative: formValues.finalProcedure === "curative",
    iocStable: formValues.intraOpCourse === "stable",
    iocReturned: formValues.intraOpCourse === "returned",
    iocUnventful: formValues.intraOpCourse === "uneventful",
    compConciouse: has(complicated, "conscious"),
    compToIcu: has(complicated, "toIcu"),
    vsIntub: formValues.ventSupport === "intubated",
    vsNo: formValues.ventSupport === "no",
    vsYes: formValues.ventSupport === "yes",
    rdr0: formValues.residualDisease === "r0",
    rdr1: formValues.residualDisease === "r1",
    rdr2: formValues.residualDisease === "r2",
    shiftedToWardId: formValues.shiftedTo?.id ?? formValues.shiftedTo?.value ?? 0,
    shiftedToWardName: formValues.shiftedTo?.label || "",
    anyComplications: formValues.anyComplications || "",
    inductionTime: toApiTime(formValues.inductionTime),
    incisionTime: toApiTime(formValues.incisionTime),
    closerTime: toApiTime(formValues.closerTime),
    extubationTime: toApiTime(formValues.extubationTime),
    proDiagnosis: formValues.proDiagnosis?.label || formValues.proDiagnosis || "",
    finalDiagnosis: formValues.finDiagnosis || "",
    cdAndGraphHandoverDetails: formValues.cdGraphHandover || "",
    implantsDetails: formValues.implantsDetails || "",
    bloodLossDetails: formValues.bloodLossDetails || "",
    tourniquet: Boolean(formValues.tourniquet),
    surgeryIds,
    assistants,
  };
};

export const applyOtNoteDetailToForm = ({
  detail,
  setValue,
  setSurgeryList,
  setAsstDoctorList,
  setAsstSurgeonList,
  setStaffList,
  theatreOptions = [],
  tableOptions = [],
  anaesthesiaOptions = [],
  otResultOptions = [],
  surgeonOptions = [],
  anaesthetistOptions = [],
}) => {
  const note = detail?.note;
  if (!note) return;

  setValue("otSheetNo", note.otSheetNo || "");
  setValue("surgeryDate", note.surgeryDate || "");
  setValue("startTime", formatDisplayTime(note.surgeryStartTime));
  setValue("endTime", formatDisplayTime(note.surgeryEndTime));
  setValue("durationMin", note.duration != null ? String(note.duration) : "");
  setValue("inductionTime", formatDisplayTime(note.inductionTime));
  setValue("incisionTime", formatDisplayTime(note.incisionTime));
  setValue("closerTime", formatDisplayTime(note.closerTime));
  setValue("extubationTime", formatDisplayTime(note.extubationTime));
  setValue("surgeryNotesText", note.surgeryNotes || "");
  setValue("anaesthetistNotesText", note.anaesthesiaNotes || "");
  setValue("recoveryText", note.recoveryObservations || "");
  setValue("remarkSurgeryPlan", note.remarks || "");
  setValue("surgeryNotesIntraOpText", note.intraOpNotes || "");
  setValue("anyComplications", note.anyComplications || "");
  setValue("finDiagnosis", note.finalDiagnosis || "");
  setValue("cdGraphHandover", note.cdAndGraphHandoverDetails || "");
  setValue("implantsDetails", note.implantsDetails || "");
  setValue("bloodLossDetails", note.bloodLossDetails || "");
  setValue("tourniquet", Boolean(note.tourniquet));
  setValue("proDiagnosis", note.proDiagnosis ? { id: note.proDiagnosis, value: note.proDiagnosis, label: note.proDiagnosis } : null);

  const theatre =
    theatreOptions.find((o) => Number(o.id) === Number(note.otId)) ||
    (note.otId
      ? toLookupOption({ id: note.otId, name: note.theatreName || String(note.otId) })
      : null);
  setValue("theatreName", theatre);

  const table =
    tableOptions.find((o) => Number(o.id) === Number(note.otTableId)) ||
    (note.otTableId
      ? toLookupOption({ id: note.otTableId, name: note.tableName || String(note.otTableId) })
      : null);
  setValue("tableName", table);

  const surgeon =
    surgeonOptions.find((o) => Number(o.id) === Number(note.surgeonId)) ||
    (note.surgeonId
      ? { id: note.surgeonId, value: note.surgeonId, label: note.surgeonName || String(note.surgeonId) }
      : null);
  setValue("surgeonName", surgeon);

  const anaesthetist =
    anaesthetistOptions.find((o) => Number(o.id) === Number(note.anaesthetistId)) ||
    (note.anaesthetistId
      ? {
          id: note.anaesthetistId,
          value: note.anaesthetistId,
          label: note.anaesthetistName || String(note.anaesthetistId),
        }
      : null);
  setValue("anaesthetistName", anaesthetist);

  const anaesthesiaIds = String(note.anaesthesiaTypeId || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  setValue(
    "anaesthesiaType",
    anaesthesiaIds.map((id) => {
      const found = anaesthesiaOptions.find((o) => String(o.id) === String(id));
      return found || { id, value: id, label: String(id) };
    })
  );

  const otResult =
    otResultOptions.find((o) => Number(o.id) === Number(note.otResultId)) ||
    (note.otResultId
      ? { id: note.otResultId, value: note.otResultId, label: String(note.otResultId) }
      : null);
  setValue("otResult", otResult);

  const drains = [];
  if (note.drainRv) drains.push("rv");
  if (note.drainAbd) drains.push("abdominal");
  if (note.drainChest) drains.push("chest");
  if (note.drainCommug) drains.push("corrugated");
  setValue("drains", drains);

  if (note.fpCurative) setValue("finalProcedure", "curative");
  else if (note.fpPallative) setValue("finalProcedure", "palliative");
  else if (note.fpExplo) setValue("finalProcedure", "exploratory");
  else setValue("finalProcedure", "");

  if (note.iocUnventful) setValue("intraOpCourse", "uneventful");
  else if (note.iocReturned) setValue("intraOpCourse", "returned");
  else if (note.iocStable) setValue("intraOpCourse", "stable");
  else setValue("intraOpCourse", "");

  const complicated = [];
  if (note.compToIcu) complicated.push("toIcu");
  if (note.compConciouse) complicated.push("conscious");
  setValue("complicated", complicated);

  if (note.vsYes) setValue("ventSupport", "yes");
  else if (note.vsNo) setValue("ventSupport", "no");
  else if (note.vsIntub) setValue("ventSupport", "intubated");
  else setValue("ventSupport", "");

  if (note.rdr0) setValue("residualDisease", "r0");
  else if (note.rdr1) setValue("residualDisease", "r1");
  else if (note.rdr2) setValue("residualDisease", "r2");
  else setValue("residualDisease", "");

  if (note.shiftedToWardId || note.shiftedToWardName) {
    setValue("shiftedTo", {
      id: note.shiftedToWardId || note.shiftedToWardName,
      value: note.shiftedToWardId || note.shiftedToWardName,
      label: note.shiftedToWardName || String(note.shiftedToWardId),
    });
  }

  setSurgeryList(
    (detail.surgeries || []).map((s) => ({
      id: `surg-${s.surgeryId}`,
      code: String(s.surgeryId),
      serviceName: s.surgeryName || s.surgeryDescription || String(s.surgeryId),
      surgeryId: s.surgeryId,
    }))
  );

  const docs = [];
  const surgeons = [];
  const staff = [];
  (detail.assistants || []).forEach((a) => {
    if (a.isDoc) {
      docs.push({
        id: `adoc-${a.asstDoctorId}`,
        code: String(a.asstDoctorId),
        serviceName: a.personName || String(a.asstDoctorId),
        doctorId: a.asstDoctorId,
      });
    } else if (a.isSurgeon) {
      surgeons.push({
        id: `asurg-${a.asstDoctorId}`,
        code: String(a.asstDoctorId),
        serviceName: a.personName || String(a.asstDoctorId),
        doctorId: a.asstDoctorId,
      });
    } else if (a.empId) {
      staff.push({
        id: `staff-${a.empId}`,
        code: String(a.empId),
        serviceName: a.personName || String(a.empId),
        empId: a.empId,
      });
    }
  });
  setAsstDoctorList(docs);
  setAsstSurgeonList(surgeons);
  setStaffList(staff);
};
